if (!customElements.get('pdp-deferred-native-video')) {
  class PdpDeferredNativeVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.video = this.querySelector('video');
      this.trigger = this.querySelector('button');
      this.src = this.dataset.videoSrc || '';
      this.loopEnabled = this.dataset.videoLoop === 'true';
      this.autoPlayOnActive = this.dataset.videoAutoplayActive === 'true';
      this.playRequested = false;
      this.posterPrimed = false;
      this.sourceAttached = false;

      if (!this.video || !this.src) return;

      this.trigger?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.startPlayback(false);
      });

      if ('IntersectionObserver' in window) {
        this.posterObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.posterObserver?.disconnect();
            this.primeLastFrame();
          },
          { rootMargin: '240px 0px' },
        );
        this.posterObserver.observe(this);
      } else {
        this.primeLastFrame();
      }

      if (this.autoPlayOnActive && 'IntersectionObserver' in window) {
        this.autoplayObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;

            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              this.startPlayback(true);
            } else {
              this.pausePlayback();
            }
          },
          { threshold: [0, 0.6, 1] },
        );
        this.autoplayObserver.observe(this);
      }
    }

    disconnectedCallback() {
      this.posterObserver?.disconnect();
      this.autoplayObserver?.disconnect();
    }

    attachSource(preload = 'metadata') {
      if (!this.sourceAttached) {
        this.video.src = this.src;
        this.sourceAttached = true;
      }
      this.video.preload = preload;
      this.video.load();
    }

    primeLastFrame() {
      if (this.posterPrimed || this.playRequested) return;
      this.posterPrimed = true;

      const seekToLastFrame = () => {
        if (this.playRequested) return;
        const duration = Number(this.video.duration);
        if (!Number.isFinite(duration) || duration <= 0) return;
        try {
          this.video.currentTime = Math.max(0, duration - 0.08);
        } catch (_) {}
      };

      this.video.addEventListener('loadedmetadata', seekToLastFrame, { once: true });
      this.video.addEventListener(
        'seeked',
        () => {
          if (!this.playRequested) this.video.pause();
        },
        { once: true },
      );

      this.attachSource('metadata');
    }

    pausePlayback() {
      if (!this.video) return;
      this.video.pause();
      this.classList.remove('is-playing');
      this.playRequested = false;
    }

    startPlayback(restart = false) {
      if (this.playRequested && !restart) return;
      this.playRequested = true;
      this.posterObserver?.disconnect();

      const playFromStart = () => {
        this.classList.add('is-playing');
        this.video.muted = true;
        this.video.controls = true;
        this.video.loop = this.loopEnabled;
        this.video.preload = 'auto';

        try {
          this.video.currentTime = 0;
        } catch (_) {}

        const promise = this.video.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(() => {
            this.playRequested = false;
            this.classList.remove('is-playing');
          });
        }
      };

      if (!this.sourceAttached) {
        this.attachSource('auto');
      } else {
        this.video.preload = 'auto';
      }

      if (this.video.readyState >= 1) {
        playFromStart();
      } else {
        this.video.addEventListener('loadedmetadata', playFromStart, { once: true });
      }
    }
  }

  customElements.define('pdp-deferred-native-video', PdpDeferredNativeVideo);
}

if (!customElements.get('pdp-native-video-thumbnail')) {
  class PdpNativeVideoThumbnail extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.video = this.querySelector('video');
      this.src = this.dataset.videoSrc || '';
      if (!this.video || !this.src) return;

      const seekToLastFrame = () => {
        const duration = Number(this.video.duration);
        if (!Number.isFinite(duration) || duration <= 0) return;
        try {
          this.video.currentTime = Math.max(0, duration - 0.08);
        } catch (_) {}
      };

      this.video.addEventListener('loadedmetadata', seekToLastFrame, { once: true });
      this.video.addEventListener(
        'seeked',
        () => {
          this.video.pause();
          this.classList.add('is-ready');
        },
        { once: true },
      );

      this.video.src = this.src;
      this.video.preload = 'metadata';
      this.video.load();
    }
  }

  customElements.define('pdp-native-video-thumbnail', PdpNativeVideoThumbnail);
}
